import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";

import { ExaminationStatusType, IExamination } from "@/models/Examination";
import { OrderByValues } from "@/root/types";
import { ExaminationsApi } from "@/api";

const useExaminations = () => {
  const [currentPage, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [order, setOrder] = useState<OrderByValues>("new");
  const [search, setSearchValue] = useState("");
  const [date, setDate] = useState<Date>();
  const [status, setStatus] = useState<ExaminationStatusType | null>(null);
  const [examinations, setExaminations] = useState<IExamination[]>([]);
  const [loading, setLoading] = useState(false);

  const filteredExaminations = useMemo(() => {
    if (!status) {
      return examinations;
    }
    return examinations.filter((ex) => ex.status === status);
  }, [status, examinations]);

  const getExaminations = useCallback(async () => {
    try {
      setLoading(true);
      const { pages, examinations } = await ExaminationsApi.getExaminations({
        page: currentPage,
        order,
        date,
        search,
      });
      setPages(pages);
      setExaminations(examinations);
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  }, [currentPage, order, date, search]);

  useEffect(() => {
    getExaminations();
  }, [currentPage, order, date]);

  useEffect(() => {
    let timeout: any;

    timeout = setTimeout(() => {
      getExaminations();
    }, 400);

    return () => clearTimeout(timeout);
  }, [search]);

  const handleOrderChange = (order: OrderByValues) => {
    setOrder(order);
    setPage(1);
  };

  const handleDateChange = (date: Date) => {
    setDate(date);
    setPage(1);
  };

  const handleStatusChange = (status: ExaminationStatusType) => {
    setStatus((prevStatus) => (prevStatus === status ? null : status));
  };

  const handleSearch = (event: ChangeEvent) => {
    setSearchValue((event.target as HTMLInputElement).value);
  };

  return {
    examinations: filteredExaminations,
    pages,
    order,
    status,
    currentPage,
    loading,
    setOrder: handleOrderChange,
    setDate: handleDateChange,
    setStatus: handleStatusChange,
    setPage,
    handleSearch,
  };
};

export default useExaminations;
